import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from 'antd';

const DEFAULT_SEARCH_DEBOUNCE_MS = 350;

function hasNextPage(pagination, page, pageSize, loadedCount) {
  const totalPages = pagination?.total_pages ?? pagination?.totalPages;
  if (Number.isFinite(Number(totalPages))) return page < Number(totalPages);

  const total = pagination?.total;
  if (Number.isFinite(Number(total))) return page * pageSize < Number(total);

  return loadedCount >= pageSize;
}

function mergeUniqueItems(current, next, getKey) {
  const seen = new Set();
  return [...current, ...next].filter((item) => {
    const key = getKey(item);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function isSelectScrolledToBottom(event) {
  const target = event?.target;
  if (!target) return false;
  return target.scrollTop + target.offsetHeight >= target.scrollHeight - 24;
}

function isAbortError(error) {
  return (
    error?.code === 'ERR_CANCELED'
    || error?.name === 'AbortError'
    || error?.name === 'CanceledError'
  );
}

export default function useRemoteSelectPage({
  enabled = true,
  fetchPage,
  getKey,
  onError,
  pageSize = 50,
  searchDebounceMs = DEFAULT_SEARCH_DEBOUNCE_MS,
  prefetchOnMount = false,
  prefetchSearch = '',
  selectedValue = null,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [inputSearch, setInputSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [hasMore, setHasMore] = useState(true);
  const requestRef = useRef(0);
  const searchDebounceRef = useRef(null);
  const onErrorRef = useRef(onError);
  const abortRef = useRef(null);
  const initialLoadedRef = useRef(false);
  const searchRef = useRef('');
  const openRef = useRef(false);
  const selectedValueRef = useRef(selectedValue);
  const itemsRef = useRef(items);
  const prefetchDoneRef = useRef(false);
  // Freeze prefetch intent to mount-time values. URL sync after a manual
  // selection mutates prefetchSearch/prefetchOnMount, and reacting to that
  // would reload with the UUID as the search term (showing the UUID instead
  // of the selected label).
  const prefetchOnMountRef = useRef(prefetchOnMount);
  const prefetchSearchRef = useRef(prefetchSearch);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    selectedValueRef.current = selectedValue;
  }, [selectedValue]);

  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  const loadPage = useCallback(
    async ({ page: nextPage = 1, search: nextSearch = '', append = false } = {}) => {
      if (!enabled) {
        setItems([]);
        setPage(1);
        setInputSearch('');
        setAppliedSearch('');
        setHasMore(false);
        initialLoadedRef.current = false;
        return;
      }

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      const requestId = requestRef.current + 1;
      requestRef.current = requestId;

      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      try {
        const { items: nextItems = [], pagination } = await fetchPage({
          page: nextPage,
          limit: pageSize,
          search: nextSearch,
          signal: controller.signal,
        });
        if (requestRef.current !== requestId || controller.signal.aborted) return;

        const safeItems = Array.isArray(nextItems) ? nextItems : [];
        setItems((prev) => {
          let next = append ? mergeUniqueItems(prev, safeItems, getKey) : safeItems;
          const activeValue = selectedValueRef.current;
          if (!append && activeValue != null && activeValue !== '') {
            const pinned = prev.find((item) => String(getKey(item)) === String(activeValue));
            if (pinned) {
              next = mergeUniqueItems([pinned], next, getKey);
            }
          }
          return next;
        });
        setPage(nextPage);
        if (!append) {
          setAppliedSearch(nextSearch);
          searchRef.current = nextSearch;
        }
        setHasMore(hasNextPage(pagination, nextPage, pageSize, safeItems.length));
        if (nextPage === 1) initialLoadedRef.current = true;
      } catch (e) {
        if (isAbortError(e) || requestRef.current !== requestId) return;
        if (!append) setItems([]);
        setHasMore(false);
        onErrorRef.current?.(e);
      } finally {
        if (requestRef.current === requestId) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [enabled, fetchPage, getKey, pageSize]
  );

  useEffect(() => {
    if (!enabled) {
      abortRef.current?.abort();
      setItems([]);
      setPage(1);
      setInputSearch('');
      setAppliedSearch('');
      setHasMore(true);
      initialLoadedRef.current = false;
      searchRef.current = '';
      prefetchDoneRef.current = false;
    }
  }, [enabled]);

  useEffect(() => {
    abortRef.current?.abort();
    clearTimeout(searchDebounceRef.current);
    setItems([]);
    setPage(1);
    setInputSearch('');
    setAppliedSearch('');
    searchRef.current = '';
    setHasMore(true);
    initialLoadedRef.current = false;
  }, [fetchPage]);

  // Prefetch only once per enabled cycle using mount-time intent only — never
  // re-run when prefetchSearch/prefetchOnMount change (e.g. URL sync after a
  // manual selection would otherwise set searchValue to a UUID).
  useEffect(() => {
    if (!prefetchOnMountRef.current || !enabled || prefetchDoneRef.current) return undefined;
    prefetchDoneRef.current = true;
    loadPage({ page: 1, search: prefetchSearchRef.current, append: false });
    return undefined;
  }, [enabled, loadPage]);

  useEffect(
    () => () => {
      abortRef.current?.abort();
      clearTimeout(searchDebounceRef.current);
    },
    []
  );

  const loadMore = useCallback(() => {
    if (!hasMore || loading || loadingMore) return;
    loadPage({ page: page + 1, search: searchRef.current, append: true });
  }, [hasMore, loading, loadingMore, loadPage, page]);

  const clearSearch = useCallback(() => {
    clearTimeout(searchDebounceRef.current);
    searchRef.current = '';
    setInputSearch('');
    setAppliedSearch('');
    initialLoadedRef.current = false;
    loadPage({ page: 1, search: '', append: false });
  }, [loadPage]);

  const handleOpenChange = useCallback(
    (nextOpen) => {
      openRef.current = nextOpen;
      if (!nextOpen || !enabled) return;

      if (itemsRef.current.length === 0) {
        searchRef.current = '';
        loadPage({ page: 1, search: '', append: false });
        return;
      }

      if (!initialLoadedRef.current) {
        loadPage({ page: 1, search: searchRef.current, append: false });
      }
    },
    [enabled, loadPage]
  );

  const handleSearch = useCallback(
    (value) => {
      clearTimeout(searchDebounceRef.current);
      const nextSearchTrimmed = value.trim();
      const prevSearch = searchRef.current;
      setInputSearch(value);
      searchDebounceRef.current = setTimeout(() => {
        if (!nextSearchTrimmed && !openRef.current && initialLoadedRef.current) {
          return;
        }
        if (!nextSearchTrimmed && openRef.current && !prevSearch && initialLoadedRef.current) {
          return;
        }
        initialLoadedRef.current = false;
        loadPage({ page: 1, search: nextSearchTrimmed, append: false });
      }, searchDebounceMs);
    },
    [loadPage, searchDebounceMs]
  );

  const handlePopupScroll = useCallback(
    (event) => {
      if (!hasMore || loading || loadingMore || !isSelectScrolledToBottom(event)) return;
      loadMore();
    },
    [hasMore, loading, loadingMore, loadMore]
  );

  const dropdownRender = useCallback(
    (menu) => (
      <>
        {appliedSearch ? (
          <div className="flex items-center justify-between gap-2 border-b border-gray-100 px-3 py-2">
            <span className="truncate text-xs text-gray-500">
              Showing results for &ldquo;{appliedSearch}&rdquo;
            </span>
            <Button
              type="link"
              size="small"
              disabled={loading}
              onMouseDown={(e) => e.preventDefault()}
              onClick={clearSearch}
            >
              Clear search
            </Button>
          </div>
        ) : null}
        {menu}
        {hasMore ? (
          <div className="border-t border-gray-100 px-3 py-2 text-center">
            <Button
              type="link"
              size="small"
              loading={loadingMore}
              disabled={loading}
              onMouseDown={(e) => e.preventDefault()}
              onClick={loadMore}
            >
              Load more
            </Button>
          </div>
        ) : null}
      </>
    ),
    [appliedSearch, hasMore, loadingMore, loading, loadMore, clearSearch]
  );

  return {
    items,
    loading,
    loadingMore,
    hasMore,
    searchValue: inputSearch,
    handleSearch,
    handlePopupScroll,
    handleOpenChange,
    clearSearch,
    dropdownRender,
  };
}
